import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CommonValidators } from '@edl-development/edl-common';
import { HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL } from 'src/app/constants/a48.constants';
import { TimeTrackingDetail } from 'src/app/model/cpl-time-tracking.model';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { TimeTrackingEventTypes } from '../../enums/time-tracking-event-types.enum';
import { merge } from 'rxjs';

const TRIGGERS_REQUIRED_VALIDATORS= ['Applicable', 'EventType'];

@Injectable()
export class TimeTrackingFormService {

    constructor(
        private formBuilder: UntypedFormBuilder
    ) {}

    public buildForm(flexDetails: TimeTrackingDetail | undefined | null, isComplianceItem?: boolean) {
        const timeTrackingForm = this.formBuilder.group({
            Applicable: [flexDetails?.Applicable ?? false, []],
            Hours: [(flexDetails?.Hours ?? 0) > 0 ? flexDetails.Hours : null, [this.hoursValidators()]],
            EventName: [flexDetails?.EventName ?? '', !isComplianceItem ? [this.IsRequiredValidator()] : []],
            EventStartDate: [flexDetails?.EventStartDate ?? null, !isComplianceItem ? [this.IsRequiredValidator()] : []],
            EventEndDate: [flexDetails?.EventEndDate ?? null, !isComplianceItem ? [this.IsRequiredValidator()] : []],
            EventType: [flexDetails?.EventType == TimeTrackingEventTypes.USER_BASED, []],
            TimeType: [flexDetails?.TimeType ?? TimeTrackingTypes.FLEX, []],
            Confirmed: [flexDetails?.Confirmed ?? false, []]
        });

        const timeTrackingSubscription = merge(
            ...TRIGGERS_REQUIRED_VALIDATORS.map(field => timeTrackingForm.get(field).valueChanges)
        )
        .subscribe(() => this.triggerIsRequiredValidators(timeTrackingForm));

        return { timeTrackingForm, timeTrackingSubscription };
    }

    public packageForm(form: AbstractControl): TimeTrackingDetail {
        const value = form.value;
        
        return {
            ItemID: "",
            RequestID: "",
            Applicable: value.Applicable,
            Hours: value.Hours,
            EventName: value.EventName,
            EventStartDate: value.EventStartDate,
            EventEndDate: value.EventEndDate,
            EventType: value.EventType ? TimeTrackingEventTypes.USER_BASED : TimeTrackingEventTypes.ITEM_BASED,
            TimeType: value.TimeType,
            Confirmed: value.Confirmed || false
        };
    }

    private triggerIsRequiredValidators(form: UntypedFormGroup) {
        const controls = Object.keys(form.controls)
            .filter(k => !TRIGGERS_REQUIRED_VALIDATORS.includes(k))
            .map(k => form.get(k));

        controls.forEach(c => c.updateValueAndValidity({ onlySelf: true, emitEvent: false }));
    }

    private hoursValidators = () => {
        return (formControl: AbstractControl) => {
            const parent = formControl.parent;
            if (parent && this.isRequired(parent)) {
                return Validators.compose([
                    Validators.required,
                    CommonValidators.isValidDecimal(HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL)
                ])(formControl);
            }

            return null;
        }
    }

    private IsRequiredValidator = () => {
        return (formControl: AbstractControl) => {
            const parent = formControl.parent;
            if (parent && this.isRequired(parent)) {
                return Validators.required(formControl);
            }

            return null;
        }
    }

    public isApplicable = (form: AbstractControl) => form.get('Applicable').value;

    private isUserEventType = (form: AbstractControl) => !!form.get('EventType').value;

    public isRequired = (form: AbstractControl) => this.isApplicable(form) && !this.isUserEventType(form);
}
